import React, { ReactElement, useRef, useState } from 'react';
import clsx, { ClassValue } from 'clsx';
import MaskInput from 'react-input-mask';
import { StyledFormFeedback } from '../formFeedback';
import { phoneTypeNameForShortCode } from 'utils/constants/Telephony/telephonyConstants';

type Selection = {
  end: number;
  start: number;
  length: number;
};

type MaskedStateType = {
  selection: Selection;
  value: string;
};

type MaskInputType = {
  id?: string;
  value?: string | any;
  disabled?: boolean;
  placeHolder?: string;
  phoneTypeName?: string;
  className?: ClassValue;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  onPaste?: (value: string) => void;
  onValidChange?: () => void;
  type?:
  | 'number'
  | 'search'
  | 'time'
  | 'text'
  | 'email'
  | 'password'
  | 'tel'
  | 'url'
  | 'date'
  | undefined;
  pattern?: string;
  feedback?: string;
};

const maskCharCountForUs = 12;
const maskCharCountForOthers = 15;
const maskCharCountForShortCode = 6;

function InputMask({
  id,
  value,
  disabled,
  className,
  phoneTypeName,
  type,
  onChange,
  pattern,
  onBlur,
  onPaste,
  onValidChange,
  feedback,
}: MaskInputType): ReactElement {

  const [hasUserInput, setHasUserInput] = useState(false);
  const [internalError, setInternalError] = useState<string | null>(null);
  const previousValue = useRef(value || '');

  // Function to validate the input based on pattern
  const isValidInput = (inputValue: string): boolean => {
    if (!inputValue || !pattern) return false;

    const cleanValue = inputValue.replace(/[^0-9]/g, '');

    switch (pattern) {
      case '999-999-9999':
        return cleanValue.length === 10;
      case '999999':
        return cleanValue.length >= 5 && cleanValue.length <= 6;
      default:
        return cleanValue.length >= 8 && cleanValue.length <= 15;
    }
  };
  //To restrict removing characters from starting of input

  const beforeMaskedValueChange = (
    newState: MaskedStateType,
    oldState: MaskedStateType,
    inputValue: any,
  ): MaskedStateType => {
    const maxMaskCharCount =
      pattern === '999-999-9999'
        ? maskCharCountForUs
        : pattern === '999999'
          ? maskCharCountForShortCode
          : maskCharCountForOthers;

    if (inputValue) {
      const userInput = inputValue?.replace(/[^0-9]/g, '');
      if (phoneTypeName === phoneTypeNameForShortCode) {
        if (oldState.selection.start === 0 && userInput.startsWith('1')) {
          return oldState;
        }

        // Additional check: if the resulting value would start with '1', reject it
        const { value, selection } = oldState;
        const potentialNewValue = copyPasteString(
          value,
          selection.start,
          userInput,
          selection.length,
        );
        const cleanPotentialValue = potentialNewValue.replace(/[^0-9]/g, '');

        if (cleanPotentialValue.startsWith('1')) {
          return oldState; // Reject the input
        }
      }
      const { value, selection } = oldState;
      newState = {
        ...newState,
        value:
          value.length < maxMaskCharCount
            ? copyPasteString(
              value,
              selection.start,
              userInput,
              selection.length,
            )
            : oldState.value,
      };
    }

    return {
      ...newState,
      value:
        pattern === '999-999-9999'
          ? formatPhnumber(newState.value)
          : newState.value.replace(/([^\w ]|_)/g, '').replace(/ /g, ''),
    };
  };

  const formatPhnumber = (phnnumber: string) => {
    let formattedStr = '';
    const phnNumber = phnnumber.replace(/([^\w ]|_)/g, '').replace(/ /g, '');
    const digitCount =
      pattern === '999-999-9999'
        ? maskCharCountForUs - 2
        : pattern === '999999'
          ? maskCharCountForShortCode
          : maskCharCountForOthers;

    for (let i = 0; i < phnNumber.length && i < digitCount; i++) {
      if (
        (i == 3 && phnNumber.length >= 3) ||
        (i == 6 && phnNumber.length >= 7)
      ) {
        formattedStr += '-';
      }
      formattedStr += phnNumber[i];
    }
    return formattedStr;
  };

  const copyPasteString = (
    str: string,
    start: number,
    input: string,
    length: number,
  ): string => {
    return str.substring(0, start) + input + str.substring(start + length);
  };

  const handleChange = (e: any) => {
    const newValue = e.target.value;

    // Check if user typed something
    if (newValue !== previousValue.current) {
      setHasUserInput(true);
      previousValue.current = newValue;

      // Clear internal error immediately when user starts typing after an error
      if (internalError && newValue !== value) {
        setInternalError(null);
      }

      // If the new value is valid and we had an error before, trigger validation
      if (feedback && isValidInput(newValue)) {
        setTimeout(() => {
          onValidChange && onValidChange();
        }, 0);
      }
    }
    onChange && onChange(e);
  };

  const handleBlur = (e: any) => {
    const currentValue = e.target.value;

    // Only show internal validation if user has typed something and there's no external feedback
    if (hasUserInput && !feedback) {
      const isValid = isValidInput(currentValue);
      if (!isValid) {
        // Set internal error message based on pattern
        if (pattern === '999999') {
          setInternalError('Please enter a short code with 5 or 6 digits that does not start with 1.');
        } else if (pattern === '999-999-9999') {
          setInternalError('Please enter a valid phone number (e.g., xxx-xxx-xxxx)');
        } else {
          setInternalError('Please enter a valid phone number between 8 and 15 digits');
        }
      } else {
        setInternalError(null);
      }
    }
    onBlur && onBlur(e);
  };

  const userClasses = clsx(className);

  // Show external feedback (from react-hook-form) first, then internal error
  const displayFeedback = feedback || internalError;

  return (
    <>
      <MaskInput
        id={id}
        mask={pattern}
        type={type}
        disabled={disabled}
        className={userClasses}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        onPaste={onPaste}
        beforeMaskedValueChange={beforeMaskedValueChange}
      />
      {displayFeedback && (
        <StyledFormFeedback validation="error">{displayFeedback}</StyledFormFeedback>)}
    </>
  );
}

export { InputMask };
